// Client helpers: API calls, tiny router, formatting, data hooks.
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
export { dollars, hours, WO_STATUS_VIEW as WO_STATUSES, PAY_TYPES } from '../shared/calc';
import { dollars as fmtMoney } from '../shared/calc';

export type Role = 'owner' | 'manager' | 'mechanic';
export type Me = { user: { id: string; login: string; name: string; role: Role; mustChange?: boolean; support?: 'view' | 'edit'; perms?: Record<string, boolean>; supervisor?: number; counter?: number; floor?: Record<string, boolean> }; shop: Record<string, string>; platform?: string };
export type Row = Record<string, any>;

export class ApiError extends Error { constructor(public status: number, msg: string) { super(msg); } }

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const isForm = opts.body instanceof FormData;
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method || (opts.body ? 'POST' : 'GET'),
      headers: isForm || opts.body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: isForm ? (opts.body as FormData) : opts.body === undefined ? undefined : JSON.stringify(opts.body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError(0, 'No connection. Check your internet and try again.');
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && path !== '/api/me' && path !== '/api/login') { window.dispatchEvent(new Event('turan:signed-out')); }
  if (!res.ok) throw new ApiError(res.status, data.error || 'Something went wrong');
  // Something went to the trash — offer "Undo" for a few seconds.
  if (data && typeof data === 'object' && typeof (data as { trash_id?: unknown }).trash_id === 'string')
    window.dispatchEvent(new CustomEvent('turan:trashed', { detail: (data as { trash_id: string }).trash_id }));
  return data as T;
}

/** Load data from the API; returns [data, error, reload]. */
export function useApi<T = any>(path: string | null, deps: unknown[] = []) {
  const [state, setState] = useState<{ data: T | null; error: string; loading: boolean }>({ data: null, error: '', loading: true });
  const load = useCallback(async () => {
    if (!path) return;
    setState(s => ({ ...s, loading: true }));
    try { setState({ data: await api<T>(path), error: '', loading: false }); }
    catch (e) { setState(s => ({ ...s, error: (e as Error).message, loading: false })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);
  useEffect(() => { void load(); }, [load]);
  return { ...state, reload: load };
}

// ─── Preview mode ───
// The browser preview (scripts/build-preview.mjs) runs the whole app in one page with sample data.
// It sets window.__TURAN_DEMO__ before the app loads; production never does.
type Demo = { start?: string; assets: Record<string, string>; photo: (id: string) => string };
export const DEMO: Demo | undefined = (window as unknown as { __TURAN_DEMO__?: Demo }).__TURAN_DEMO__;
export const asset = (p: string) => DEMO?.assets[p] || p;
export const photoSrc = (id: string) => (DEMO ? DEMO.photo(id) : `/api/photos/${id}/file`);

// ─── Router (history API; in-memory in preview mode) ───
const listeners = new Set<() => void>();
let virtual = DEMO?.start || '/';
const currentHref = () => (DEMO ? virtual : location.pathname + location.search);
/** True when the page was opened from the + Create menu (…?new=1): open its add form straight away. */
export const openedForNew = () => new URLSearchParams(currentHref().split('?')[1] || '').get('new') === '1';
window.addEventListener('popstate', () => listeners.forEach(l => l()));
/** Preview mode only: jump to a path without history (used when switching the preview's role). */
export function resetPreviewPath(to: string) { virtual = to; listeners.forEach(l => l()); }
export function navigate(to: string, replace = false) {
  if (to === currentHref()) return;
  if (DEMO) virtual = to;
  else history[replace ? 'replaceState' : 'pushState'](null, '', to);
  listeners.forEach(l => l());
  window.scrollTo(0, 0);
}
/** Preview only: lets scripted demo recordings jump to a page and read where they are. */
if (DEMO) (window as unknown as { __pvNav: unknown }).__pvNav = { go: (to: string) => navigate(to), href: currentHref };
export function useLocation() {
  const href = useSyncExternalStore(cb => (listeners.add(cb), () => listeners.delete(cb)), currentHref);
  const u = new URL(href, location.origin);
  return { path: u.pathname, query: u.searchParams };
}
/** Match '/work-orders/:id' against a path. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const keys: string[] = [];
  const re = new RegExp('^' + pattern.replace(/:(\w+)/g, (_, k) => (keys.push(k), '([^/]+)')) + '/?$');
  const m = path.match(re);
  return m ? Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1])])) : null;
}
export function setQuery(params: Record<string, string>) {
  const u = new URL(currentHref(), location.origin);
  for (const [k, val] of Object.entries(params)) val ? u.searchParams.set(k, val) : u.searchParams.delete(k);
  const s = u.searchParams.toString();
  navigate(u.pathname + (s ? '?' + s : ''), true);
}

// ─── Formatting / parsing ───
export const toCents = (s: unknown) => Math.round(Number(String(s ?? '').replace(/[$,\s]/g, '') || 0) * 100);
export const toHundredths = (s: unknown) => Math.round(Number(String(s ?? '').replace(/[,\s]/g, '') || 0) * 100);
export const centsInput = (c: number | undefined) => (c ? (c / 100).toFixed(2) : '');
export const bpInput = (bp: number | string | undefined) => (Number(bp || 0) / 100).toString();

export const TZ = 'America/New_York';
export const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
export const addDays = (d: string, n: number) => new Date(Date.parse(d + 'T12:00:00Z') + n * 86400000).toISOString().slice(0, 10);
export function fmtDate(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s.length === 10 ? s + 'T12:00:00' : s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: s.length === 10 ? undefined : TZ });
}
export function fmtTime(s?: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: TZ });
}
export function fmtClock(s?: string | null) {
  if (!s) return '—';
  return new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ });
}
export function ago(s?: string | null) {
  if (!s) return '';
  const m = Math.round((Date.now() - Date.parse(s)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
/** ISO timestamp → value for <input type="datetime-local"> in shop time. */
export function toLocalInput(iso?: string | null) {
  if (!iso) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(iso)).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
/** <input type="datetime-local"> value (shop time) → ISO UTC. */
export function fromLocalInput(v: string) {
  if (!v) return '';
  // Find the UTC offset of the shop zone at that wall-clock time.
  const guess = new Date(v + ':00Z');
  const asShop = new Date(toLocalInput(guess.toISOString()) + ':00Z');
  return new Date(guess.getTime() + (guess.getTime() - asShop.getTime())).toISOString();
}

export function downloadCsv(name: string, heads: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (x: unknown) => { const s = String(x ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const blob = new Blob([[heads, ...rows].map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** Shrink a phone photo before upload: max 1600px, JPEG ~80%. Keeps uploads fast on shop Wi-Fi / cell data. */
export async function compressImage(file: File, max = 1600, stamp = ''): Promise<Blob> {
  if (!file.type.startsWith('image/') && !/\.(jpe?g|png|heic|heif|webp)$/i.test(file.name)) return file;
  let bmp: ImageBitmap | HTMLImageElement | null = null;
  try {
    // iPhone / iPad: a full camera photo is big — decode it, draw it small, free the memory right away
    // (a phone that runs out of memory here reloads the page white).
    bmp = await loadPicture(file);
    const w = bmp.width, h = bmp.height;
    const scale = Math.min(1, max / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const g = canvas.getContext('2d')!;
    g.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    if ('close' in bmp) bmp.close();
    bmp = null;
    // Date / time / mileage printed on the picture itself — proof of how the truck came in.
    if (stamp) {
      const fs = Math.max(14, Math.round(canvas.width / 42));
      g.font = `600 ${fs}px system-ui, -apple-system, Segoe UI, Arial, sans-serif`;
      const pad = Math.round(fs * 0.5), h = fs + pad * 2;
      g.fillStyle = 'rgba(0,0,0,.6)'; g.fillRect(0, canvas.height - h, canvas.width, h);
      g.fillStyle = '#fff'; g.textBaseline = 'middle'; g.fillText(stamp, pad, canvas.height - h / 2, canvas.width - pad * 2);
    }
    const blob = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.8));
    canvas.width = canvas.height = 0; // let iOS free the canvas now
    return blob && (stamp || blob.size < file.size || /heic|heif/i.test(file.type)) ? blob : file;
  } catch {
    return file;
  } finally {
    if (bmp && 'close' in bmp) bmp.close();
  }
}

/** Decode a photo: ImageBitmap where the browser has it, otherwise an <img> (older iPhones). */
async function loadPicture(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') { try { return await createImageBitmap(file); } catch { /* fall back to <img> */ } }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}

export async function uploadPhoto(file: File, fields: { kind: string; work_order_id?: string; note?: string; purpose?: string; folder?: string; job_id?: string; shot?: string; stamp?: string }, max = 1600) {
  const fd = new FormData();
  const blob = await compressImage(file, max, fields.stamp || '');
  fd.set('file', blob, file.name.replace(/\.\w+$/, '') + (blob.type === 'image/jpeg' ? '.jpg' : ''));
  for (const [k, val] of Object.entries(fields)) if (val) fd.set(k, val);
  return api<{ id: string }>('/api/photos', { body: fd });
}

/** A photo of a whole page cut into a top and a bottom part (they overlap), so small print is big enough to read. */
export async function pageTiles(file: File): Promise<File[]> {
  if (!file.type.startsWith('image/')) return [];
  try {
    const bmp = await createImageBitmap(file);
    const tall = bmp.height >= bmp.width;
    const out: File[] = [];
    for (const [a, b] of [[0, 0.58], [0.42, 1]]) {
      const sx = tall ? 0 : Math.round(bmp.width * a), sy = tall ? Math.round(bmp.height * a) : 0;
      const sw = tall ? bmp.width : Math.round(bmp.width * (b - a)), sh = tall ? Math.round(bmp.height * (b - a)) : bmp.height;
      const scale = Math.min(1, 2400 / Math.max(sw, sh));
      const cv = document.createElement('canvas'); cv.width = Math.round(sw * scale); cv.height = Math.round(sh * scale);
      cv.getContext('2d')!.drawImage(bmp, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
      const blob = await new Promise<Blob | null>(r => cv.toBlob(r, 'image/jpeg', 0.9));
      if (blob) out.push(new File([blob], `part-${out.length + 1}.jpg`, { type: 'image/jpeg' }));
    }
    return bmp.width * bmp.height > 1_500_000 ? out : [];
  } catch { return []; }
}

/** AI reads a photo (vin / company / part) — nothing is saved, the answer fills a form for the person to check. */
export async function readPhoto(kind: 'vin' | 'company' | 'part', file: File) {
  const fd = new FormData();
  const blob = await compressImage(file, 2000);
  fd.set('file', blob, 'photo.jpg');
  fd.set('kind', kind);
  return api<Row>('/api/ai/read-photo', { body: fd });
}

/** The shop's payment methods (Settings → Shop & billing): Cash, Check, Card, ACH, Zelle … plus any they add (Wire, EFS …). */
export const DEFAULT_METHODS = ['Cash', 'Check', 'Card', 'ACH', 'Zelle', 'Other'];
export function payMethods(): string[] {
  try { const a = JSON.parse(CURRENT?.shop?.payment_methods || ''); if (Array.isArray(a) && a.length) return a.map(String); } catch { /* default list */ }
  return DEFAULT_METHODS;
}
export const statusTone = (s: string) =>
  ({ New: 'blue', Diagnosing: 'violet', 'Waiting Parts': 'amber', 'In Progress': 'teal', Ready: 'green', Completed: 'gray', Paid: 'green', Unpaid: 'amber', Partial: 'amber', Overdue: 'red', Void: 'gray', Editing: 'violet', 'Waiting Approval': 'red', Draft: 'amber', Urgent: 'red' } as Record<string, string>)[s] || 'gray';

/** Grace days before an unpaid invoice shows as Overdue (the portal sets it from the shop's settings). */
let GRACE: string | undefined;
export const setGrace = (g: unknown) => { GRACE = g === undefined || g === null ? undefined : String(g); };
export const isOverdueDate = (due: string) => due < overdueBefore(today(), GRACE ?? CURRENT?.shop?.overdue_grace);
export function invoiceStatus(i: { total: number; paid: number; void: number; due_on: string }) {
  if (i.void) return 'Void';
  if (i.paid >= i.total) return 'Paid';
  if (i.due_on < overdueBefore(today(), GRACE ?? CURRENT?.shop?.overdue_grace)) return 'Overdue';
  return i.paid > 0 ? 'Partial' : 'Unpaid';
}

/** After adding a part: say so when a tire disposal fee came with it. */
/** "Goes with" parts that came along with a part: "Also added: Harness × 1 (0 left — order it)". */
export function linkedNote(r: unknown) {
  const l = (r as { linked?: { name: string; sku: string; qty: number; left: number | null }[] } | null)?.linked || [];
  if (!l.length) return '';
  return '🔗 Also added: ' + l.map(x => `${x.name || x.sku} × ${x.qty}${x.left !== null && x.left < 0 ? ' (not enough on the shelf — order it)' : x.left === 0 ? ' (last one — order more)' : ''}`).join(', ') + ' — remove it on its line if not needed';
}
export function disposalNote(r: unknown, prices = true) {
  const x = r as { disposal?: { qty: number; fee: number } | null; fees?: { title: string; qty: number; amount: number }[] } | null;
  const d = x?.disposal;
  const all = [...(d ? [{ title: 'Tire disposal fee', qty: d.qty, amount: d.fee }] : []), ...(x?.fees || [])];
  if (!all.length) return '';
  // Mechanics don't see prices or fee lines — only what matters on the floor.
  if (!prices) return x?.fees?.some(f => /^Core charge/.test(f.title)) ? 'Part recorded — this part has a core: keep the old one for return' : 'Part recorded on this job';
  return `${all.map(f => `${f.title}${prices ? ` (${f.qty} × ${fmtMoney(f.amount)})` : ''}`).join(', ')} added — change or delete on its line`;
}

/** Everything a part can be found by: number, name, bin, vendor, search words (any language), other part numbers, barcode, brand, category. */
export const partSearchText = (p: Row) => [p.sku, p.name, p.bin, p.supplier, p.search_tags, p.alt_numbers, p.upc, p.manufacturer, p.category].filter(Boolean).join(' ').toLocaleLowerCase();
/** Every typed word must match somewhere. */
/** "WAB 441-030 905.2" and "wab4410309052" are the same part number. */
export const normKey = (s: unknown) => partKey(s);
import { partKey, cleanPasted } from '../shared/text';
export { cleanPasted };
export const partMatches = (p: Row, q: string) => {
  const t = partSearchText(p);
  q = cleanPasted(q).trim();
  if (q.toLocaleLowerCase().split(/\s+/).filter(Boolean).every(w => t.includes(w))) return true;
  const k = normKey(q);
  return k.length >= 3 && normKey(t).includes(k);
};

// The signed-in person, so any screen can ask "may they see this?" (e.g. part costs).
let CURRENT: Me | null = null;
export const setCurrentMe = (m: Me | null) => { CURRENT = m; };
export const currentMe = () => CURRENT;
export const mayI = (k: import('../shared/perms').PermKey) => canDo(CURRENT?.user, k);
/** Shop-floor switch of the signed-in person's role (office people: always yes). */
export const floorOk = (k: import('../shared/perms').FloorKey) => floorCanDo(CURRENT?.user, k);
import { can as canDo, floorCan as floorCanDo } from '../shared/perms';
import { overdueBefore } from '../shared/calc';

/** Upload a receipt photo and let AI read it: { photo_id, store, date, total (cents), category, description }. */
export async function scanReceipt(file: File) {
  const { id } = await uploadPhoto(file, { kind: 'receipt', purpose: 'expense' });
  const r = await api<{ store: string; date: string; total: number; tax: number; category: string; description: string }>('/api/scan/receipt', { body: { photo_id: id } });
  return { photo_id: id, ...r };
}
